import { Box, Collapse } from '@mui/material'
import { useState } from 'react'
import StorageIcon from '@mui/icons-material/Storage'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { TOKENS, FONT_SANS, FONT_MONO } from './OptimiserDesign'
import { SECTION_GROUPS, SOLVER_SECTIONS, SectionGroup, TableSection } from './Optimiser.sections'

const selectedSx = (on: boolean) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  px: '10px',
  py: '5px',
  mb: '1px',
  borderRadius: '2px',
  cursor: 'pointer',
  borderLeft: '3px solid',
  borderColor: on ? TOKENS.brandTeal : 'transparent',
  background: on ? 'rgba(0,65,90,0.08)' : 'transparent',
  color: on ? TOKENS.brandTeal : TOKENS.ink700,
  fontSize: 12.5,
  fontWeight: on ? 600 : 400,
  fontFamily: FONT_SANS,
  '&:hover': { background: on ? 'rgba(0,65,90,0.08)' : 'rgba(0,65,90,0.04)' },
})

const SubDivider = ({ label }: { label: string }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 0.75,
      px: '10px',
      pt: 1,
      pb: 0.5,
    }}
  >
    <Box
      component="span"
      sx={{
        fontSize: 8.5,
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
        color: TOKENS.ink300,
      }}
    >
      {label}
    </Box>
    <Box sx={{ flex: 1, height: '1px', background: TOKENS.line200 }} />
  </Box>
)

const StageHeader = ({
  num,
  label,
  icon,
  open,
  onToggle,
}: {
  num: string
  label: string
  icon: React.ReactNode
  open: boolean
  onToggle: () => void
}) => (
  <Box
    onClick={onToggle}
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      mx: 1.5,
      mt: 1.5,
      mb: 0.75,
      pb: 0.75,
      borderBottom: '2px solid',
      borderColor: TOKENS.brandTeal,
      cursor: 'pointer',
    }}
  >
    <Box
      component="span"
      sx={{
        width: 16,
        height: 16,
        borderRadius: '50%',
        background: TOKENS.brandTeal,
        color: '#fff',
        fontSize: 9.5,
        fontWeight: 700,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: FONT_MONO,
        flexShrink: 0,
      }}
    >
      {num}
    </Box>
    <Box sx={{ color: TOKENS.brandTeal, display: 'flex', '& svg': { fontSize: 15 } }}>
      {icon}
    </Box>
    <Box
      component="span"
      sx={{
        flex: 1,
        fontSize: 10,
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
        color: TOKENS.brandTeal,
        fontFamily: FONT_SANS,
      }}
    >
      {label}
    </Box>
    {open ? (
      <ExpandLessIcon sx={{ fontSize: 15, color: TOKENS.ink400 }} />
    ) : (
      <ExpandMoreIcon sx={{ fontSize: 15, color: TOKENS.ink400 }} />
    )}
  </Box>
)

interface SidebarProps {
  selectedSection: string
  onSelect: (id: string) => void
}

export const OptimiserSidebar = ({ selectedSection, onSelect }: SidebarProps) => {
  const [inputsOpen, setInputsOpen] = useState(true)
  const [solverOpen, setSolverOpen] = useState(true)

  const Item = ({ item }: { item: TableSection }) => {
    const on = selectedSection === item.id
    return (
      <Box onClick={() => onSelect(item.id)} sx={selectedSx(on)}>
        <Box
          component="span"
          sx={{
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {item.label}
        </Box>
      </Box>
    )
  }

  return (
    <Box
      sx={{
        width: 230,
        minWidth: 230,
        flexShrink: 0,
        borderRight: `1px solid ${TOKENS.line300}`,
        background: TOKENS.sidebar,
        overflowY: 'auto',
        pb: 2,
        fontFamily: FONT_SANS,
      }}
    >
      <StageHeader
        num="1"
        label="Inputs"
        icon={<StorageIcon />}
        open={inputsOpen}
        onToggle={() => setInputsOpen((o) => !o)}
      />
      <Collapse in={inputsOpen}>
        <Box sx={{ px: 0.75 }}>
          {SECTION_GROUPS.map((group: SectionGroup) => (
            <Box key={group.id}>
              <SubDivider label={group.label} />
              {group.items.map((item) => (
                <Item key={item.id} item={item} />
              ))}
            </Box>
          ))}
        </Box>
      </Collapse>

      <StageHeader
        num="2"
        label="Solver"
        icon={<PlayArrowIcon />}
        open={solverOpen}
        onToggle={() => setSolverOpen((o) => !o)}
      />
      <Collapse in={solverOpen}>
        <Box sx={{ px: 0.75, pb: 0.5 }}>
          {SOLVER_SECTIONS.map((item) => (
            <Item key={item.id} item={item} />
          ))}
        </Box>
      </Collapse>
    </Box>
  )
}
